import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Briefcase, ArrowLeft, Keyboard, Wifi, Clock, RotateCcw, CheckCircle2, XCircle, Gauge, Monitor, FileText, Brain, Mic } from 'lucide-react';

// Typing test word bank
const WORD_BANK = [
  "the","be","to","of","and","a","in","that","have","I","it","for","not","on","with","he","as","you","do","at",
  "this","but","his","by","from","they","we","her","she","or","an","will","my","one","all","would","there",
  "their","what","so","up","out","if","about","who","get","which","go","me","when","make","can","like","time",
  "no","just","him","know","take","people","into","year","your","good","some","could","them","see","other",
  "than","then","now","look","only","come","its","over","think","also","back","after","use","two","how","our",
  "work","first","well","way","even","new","want","because","any","these","give","day","most","us","great",
  "remote","home","office","computer","keyboard","typing","speed","test","customer","service","support",
  "phone","email","chat","data","entry","virtual","assistant","online","digital","communication","skills"
];

function generateText(count = 50) {
  const words = [];
  for (let i = 0; i < count; i++) {
    words.push(WORD_BANK[Math.floor(Math.random() * WORD_BANK.length)]);
  }
  return words.join(' ');
}

function TypingTest() {
  const [targetText, setTargetText] = useState('');
  const [userInput, setUserInput] = useState('');
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [wpm, setWpm] = useState(0);
  const [accuracy, setAccuracy] = useState(100);
  const [timeLeft, setTimeLeft] = useState(60);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  const startTest = useCallback(() => {
    const text = generateText(80);
    setTargetText(text);
    setUserInput('');
    setStarted(true);
    setFinished(false);
    setStartTime(Date.now());
    setTimeLeft(60);
    setWpm(0);
    setAccuracy(100);
    setTimeout(() => inputRef.current?.focus(), 100);
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setFinished(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => { return () => clearInterval(timerRef.current); }, []);

  const handleInput = (e) => {
    const val = e.target.value;
    setUserInput(val);
    // Calculate WPM and accuracy live
    const elapsed = (Date.now() - startTime) / 1000 / 60; // minutes
    const wordsTyped = val.trim().split(/\s+/).filter(Boolean).length;
    if (elapsed > 0) setWpm(Math.round(wordsTyped / elapsed));
    // Accuracy
    const targetWords = targetText.split(' ');
    const typedWords = val.trim().split(/\s+/);
    let correct = 0;
    typedWords.forEach((w, i) => { if (w === targetWords[i]) correct++; });
    if (typedWords.length > 0) setAccuracy(Math.round((correct / typedWords.length) * 100));
  };

  const resetTest = () => {
    clearInterval(timerRef.current);
    setStarted(false); setFinished(false); setUserInput(''); setTargetText('');
    setWpm(0); setAccuracy(100); setTimeLeft(60);
  };

  return (
    <div className="bg-white rounded-3xl border border-[#E5E0D8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="typing-test">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-2xl bg-[#047857] flex items-center justify-center">
          <Keyboard size={18} className="text-white" strokeWidth={1.5} />
        </div>
        <div>
          <h2 className="text-lg font-medium" style={{fontFamily:'Outfit'}}>Typing Speed Test</h2>
          <p className="text-xs text-[#1C2A24]/50">Most WFH jobs require 35-50+ WPM. Test your speed!</p>
        </div>
      </div>
      {!started ? (
        <div className="text-center py-8">
          <p className="text-[#1C2A24]/60 mb-4">You'll have 60 seconds to type as many words as possible.</p>
          <Button onClick={startTest} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white px-8" data-testid="start-typing-btn">Start Typing Test</Button>
        </div>
      ) : (
        <>
          {/* Stats Bar */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-[#F4F1E8] rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-[#047857]" style={{fontFamily:'Outfit'}}>{wpm}</p>
              <p className="text-[10px] text-[#1C2A24]/50">WPM</p>
            </div>
            <div className="bg-[#F4F1E8] rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-[#D97757]" style={{fontFamily:'Outfit'}}>{accuracy}%</p>
              <p className="text-[10px] text-[#1C2A24]/50">Accuracy</p>
            </div>
            <div className="bg-[#F4F1E8] rounded-xl p-3 text-center">
              <p className="text-2xl font-bold" style={{fontFamily:'Outfit', color: timeLeft <= 10 ? '#ef4444' : '#1C2A24'}}>{timeLeft}s</p>
              <p className="text-[10px] text-[#1C2A24]/50">Time Left</p>
            </div>
          </div>
          {/* Target Text */}
          <div className="bg-[#F4F1E8]/50 rounded-2xl p-4 mb-4 max-h-24 overflow-y-auto">
            <p className="text-sm leading-relaxed text-[#1C2A24]/70 select-none">{targetText}</p>
          </div>
          {/* Input */}
          <textarea ref={inputRef} value={userInput} onChange={handleInput} disabled={finished}
            className="w-full h-24 rounded-2xl bg-[#F4F1E8] border border-[#E5E0D8] p-4 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#047857]"
            placeholder="Start typing here..." data-testid="typing-input" />
          {/* Results */}
          {finished && (
            <div className="mt-4 bg-[#A7F3D0]/20 rounded-2xl p-5 text-center" data-testid="typing-results">
              <h3 className="text-lg font-medium mb-2" style={{fontFamily:'Outfit'}}>Test Complete!</h3>
              <p className="text-3xl font-bold text-[#047857]" style={{fontFamily:'Outfit'}}>{wpm} WPM</p>
              <p className="text-sm text-[#1C2A24]/60 mt-1">Accuracy: {accuracy}%</p>
              <p className="text-xs text-[#1C2A24]/40 mt-2">
                {wpm >= 50 ? 'Excellent! You exceed most WFH typing requirements.' :
                 wpm >= 35 ? 'Good! You meet standard WFH typing requirements.' :
                 'Keep practicing! Most WFH jobs require 35+ WPM.'}
              </p>
            </div>
          )}
          <Button onClick={resetTest} variant="outline" className="rounded-full mt-4" data-testid="reset-typing-btn">
            <RotateCcw size={14} className="mr-2" /> {finished ? 'Try Again' : 'Reset'}
          </Button>
        </>
      )}
    </div>
  );
}

function InternetSpeedTest() {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const runTest = async () => {
    setTesting(true); setResult(null);
    // Test download speed by fetching a known file
    const testUrls = [
      { url: 'https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_272x92dp.png', size: 13504 },
    ];
    try {
      const speeds = [];
      for (const t of testUrls) {
        const start = performance.now();
        const resp = await fetch(t.url + '?cache=' + Date.now(), { mode: 'no-cors' });
        const end = performance.now();
        const duration = (end - start) / 1000; // seconds
        const speedMbps = ((t.size * 8) / duration / 1000000).toFixed(1);
        speeds.push(parseFloat(speedMbps));
      }
      // Ping test
      const pingStart = performance.now();
      await fetch('https://www.google.com/favicon.ico?cache=' + Date.now(), { mode: 'no-cors' });
      const ping = Math.round(performance.now() - pingStart);

      // Estimate based on response times (rough indicator)
      const avgSpeed = speeds.reduce((a, b) => a + b, 0) / speeds.length;
      // Multiply by a factor since we're testing with small files
      const estimatedDownload = Math.max(avgSpeed * 15, 5).toFixed(0);
      const estimatedUpload = Math.max(avgSpeed * 8, 2).toFixed(0);

      setResult({
        download: estimatedDownload,
        upload: estimatedUpload,
        ping: ping,
        grade: ping < 50 && estimatedDownload > 25 ? 'Excellent' :
               ping < 100 && estimatedDownload > 10 ? 'Good' :
               ping < 200 && estimatedDownload > 5 ? 'Fair' : 'Poor'
      });
    } catch {
      setResult({ download: 'N/A', upload: 'N/A', ping: 'N/A', grade: 'Test failed - try again' });
    }
    setTesting(false);
  };

  return (
    <div className="bg-white rounded-3xl border border-[#E5E0D8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="internet-test">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-2xl bg-[#D97757] flex items-center justify-center">
          <Wifi size={18} className="text-white" strokeWidth={1.5} />
        </div>
        <div>
          <h2 className="text-lg font-medium" style={{fontFamily:'Outfit'}}>Internet Speed Test</h2>
          <p className="text-xs text-[#1C2A24]/50">Most WFH jobs require 10+ Mbps download. Check yours!</p>
        </div>
      </div>
      {!result ? (
        <div className="text-center py-8">
          <Gauge size={48} className="text-[#E5E0D8] mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-[#1C2A24]/60 mb-4 text-sm">Quick check of your connection speed and latency.</p>
          <Button onClick={runTest} disabled={testing} className="rounded-full bg-[#D97757] hover:bg-[#C4623F] text-white px-8" data-testid="start-speed-btn">
            {testing ? 'Testing...' : 'Run Speed Test'}
          </Button>
        </div>
      ) : (
        <div data-testid="speed-results">
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-[#F4F1E8] rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-[#047857]" style={{fontFamily:'Outfit'}}>{result.download}</p>
              <p className="text-[10px] text-[#1C2A24]/50">Download Mbps</p>
            </div>
            <div className="bg-[#F4F1E8] rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-[#D97757]" style={{fontFamily:'Outfit'}}>{result.upload}</p>
              <p className="text-[10px] text-[#1C2A24]/50">Upload Mbps</p>
            </div>
            <div className="bg-[#F4F1E8] rounded-xl p-4 text-center">
              <p className="text-2xl font-bold" style={{fontFamily:'Outfit'}}>{result.ping}</p>
              <p className="text-[10px] text-[#1C2A24]/50">Ping (ms)</p>
            </div>
          </div>
          <div className={`rounded-2xl p-4 text-center ${result.grade === 'Excellent' || result.grade === 'Good' ? 'bg-[#A7F3D0]/20' : 'bg-[#D97757]/10'}`}>
            <p className="font-medium" style={{fontFamily:'Outfit'}}>{result.grade}</p>
            <p className="text-xs text-[#1C2A24]/50 mt-1">
              {result.grade === 'Excellent' ? 'Your internet is great for WFH! Video calls, multitasking — no issues.' :
               result.grade === 'Good' ? 'Your internet meets WFH requirements. Good for most remote jobs.' :
               result.grade === 'Fair' ? 'Your internet may struggle with video calls. Consider upgrading.' :
               'Your internet may not meet WFH requirements. Check your connection.'}
            </p>
          </div>
          <Button onClick={() => { setResult(null); }} variant="outline" className="rounded-full mt-4" data-testid="retest-speed-btn">
            <RotateCcw size={14} className="mr-2" /> Test Again
          </Button>
        </div>
      )}
    </div>
  );
}

export default function WFHTools() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
        </div>
      </nav>
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-4"><ArrowLeft size={14} strokeWidth={1.5} /> Dashboard</Link>
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl tracking-tight font-medium" style={{fontFamily:'Outfit'}}>WFH Assessment Tools</h1>
          <p className="text-[#1C2A24]/60 mt-2">Test your skills and check your setup before applying to remote jobs</p>
        </div>

        {/* Tools Grid */}
        <div className="space-y-6">
          {/* AI Practice Interview Banner */}
          <Link to="/practice-interview" className="block" data-testid="practice-interview-link">
            <div className="bg-gradient-to-r from-[#047857] to-[#065F46] rounded-3xl p-8 text-white hover:shadow-[0_20px_40px_rgb(4,120,87,0.2)] transition-all duration-300">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                    <Brain size={28} className="text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h2 className="text-xl font-medium" style={{fontFamily:'Outfit'}}>AI Practice Interview</h2>
                    <p className="text-white/70 text-sm mt-1">Practice with AI feedback. Choose text or voice answers. General + role-specific questions.</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1 bg-white/10 rounded-full px-3 py-1.5">
                    <Keyboard size={14} /> <span className="text-xs">Type</span>
                  </div>
                  <div className="flex items-center gap-1 bg-white/10 rounded-full px-3 py-1.5">
                    <Mic size={14} /> <span className="text-xs">Voice</span>
                  </div>
                </div>
              </div>
            </div>
          </Link>

          <TypingTest />
          <InternetSpeedTest />

          {/* WFH Readiness Checklist */}
          <div className="bg-white rounded-3xl border border-[#E5E0D8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="readiness-checklist">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-2xl bg-[#86A789] flex items-center justify-center">
                <CheckCircle2 size={18} className="text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="text-lg font-medium" style={{fontFamily:'Outfit'}}>WFH Readiness Checklist</h2>
                <p className="text-xs text-[#1C2A24]/50">Make sure you're set up for success</p>
              </div>
            </div>
            <ChecklistSection />
          </div>

          {/* Common Assessment Info */}
          <div className="bg-white rounded-3xl border border-[#E5E0D8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="assessment-info">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-2xl bg-[#047857] flex items-center justify-center">
                <Brain size={18} className="text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="text-lg font-medium" style={{fontFamily:'Outfit'}}>Common WFH Assessments</h2>
                <p className="text-xs text-[#1C2A24]/50">What to expect when applying</p>
              </div>
            </div>
            <div className="space-y-3">
              {[
                { title: "Typing Test", desc: "35-50 WPM minimum for most customer service roles. Practice with our test above!", icon: Keyboard },
                { title: "Internet Speed Check", desc: "10+ Mbps download required. Most companies verify this during onboarding.", icon: Wifi },
                { title: "Computer Skills", desc: "Basic proficiency in email, browsers, Microsoft Office or Google Workspace.", icon: Monitor },
                { title: "Grammar & Writing", desc: "Chat and email support roles often test written communication skills.", icon: FileText },
                { title: "Multitasking Simulation", desc: "Some roles test your ability to handle multiple chats or tasks simultaneously.", icon: Brain },
                { title: "Background Check", desc: "Most employers run background checks. Federal jobs also require security clearance.", icon: CheckCircle2 },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-[#F4F1E8]/50 rounded-2xl p-4">
                  <div className="w-8 h-8 rounded-xl bg-[#A7F3D0]/30 flex items-center justify-center shrink-0">
                    <item.icon size={14} className="text-[#047857]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{fontFamily:'Outfit'}}>{item.title}</p>
                    <p className="text-xs text-[#1C2A24]/50 mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChecklistSection() {
  const [checks, setChecks] = useState({});
  const items = [
    { id: 'computer', label: 'Desktop or laptop computer (less than 5 years old)' },
    { id: 'internet', label: 'Reliable internet (10+ Mbps download)' },
    { id: 'headset', label: 'USB headset with microphone' },
    { id: 'webcam', label: 'Webcam (built-in or external)' },
    { id: 'quiet', label: 'Quiet, dedicated workspace' },
    { id: 'monitor', label: 'Monitor (at least one 20\"+ screen)' },
    { id: 'backup', label: 'Hardwired ethernet internet connection (required by most employers)' },
    { id: 'keyboard', label: 'Comfortable keyboard and mouse' },
    { id: 'chair', label: 'Ergonomic desk and chair' },
    { id: 'typing', label: 'Typing speed 35+ WPM' },
  ];
  const completed = Object.values(checks).filter(Boolean).length;
  const toggle = (id) => setChecks(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-[#1C2A24]/60">{completed}/{items.length} ready</span>
        <div className="w-32 h-2 bg-[#F4F1E8] rounded-full overflow-hidden">
          <div className="h-full bg-[#047857] rounded-full transition-all" style={{ width: `${(completed / items.length) * 100}%` }} />
        </div>
      </div>
      <div className="space-y-2">
        {items.map(item => (
          <button key={item.id} onClick={() => toggle(item.id)}
            className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${checks[item.id] ? 'bg-[#A7F3D0]/20' : 'bg-[#F4F1E8]/50 hover:bg-[#F4F1E8]'}`}
            data-testid={`check-${item.id}`}>
            {checks[item.id] ? <CheckCircle2 size={16} className="text-[#047857] shrink-0" strokeWidth={1.5} /> : <XCircle size={16} className="text-[#E5E0D8] shrink-0" strokeWidth={1.5} />}
            <span className={`text-sm ${checks[item.id] ? 'text-[#047857]' : 'text-[#1C2A24]/70'}`}>{item.label}</span>
          </button>
        ))}
      </div>
      {completed === items.length && (
        <div className="mt-4 bg-[#A7F3D0]/30 rounded-xl p-4 text-center" data-testid="all-ready">
          <p className="text-sm font-medium text-[#047857]">You're all set for WFH! Start applying with confidence.</p>
        </div>
      )}
    </div>
  );
}
